import { ActionTree, Commit } from 'vuex';
import { ILookupResult } from '../shared-types';
import { IFundSearchState, Timeout } from './types';
import { fetchFundSearchResults, FundSearchCache } from './utils';
import { setupClassCommon, unsetClassCommon } from './utils/class-auth-fetch-service';

const debounceTime = 500;
const timeoutHandle: { debounce: Timeout } = { debounce: 0 };

export async function performSearch(
  searchText: string,
  commit: Commit,
  getters: any
) {
  if (searchText.length < 3) {
    return;
  }
  commit('setLastFetchError', null);
  const cacheSuffix = getters['cacheSuffix'] + getters.businessCode;

  const cache = new FundSearchCache({ keySuffix: cacheSuffix });
  const cachedFundSearchResults = cache.get(searchText);
  if (cachedFundSearchResults) {
    commit('setSelectedResultIndex', -1);
    commit('results', cachedFundSearchResults);
    commit('lookup', searchText);
    return;
  }

  commit('setNumberOfFetching', getters['numberOfFetching'] + 1);
  // console.log('fetching: ' + getters['numberOfFetching']);
  const apiEndpoint = getters['fundSearchApiEndpoint'];
  const accessToken = getters['accessToken'];
  const promiseFetchFundSearchResults = fetchFundSearchResults(
    searchText,
    apiEndpoint,
    accessToken
  );

  commit('lookup', searchText);
  let err: string | null = null;
  let fundSearchResults: ILookupResult[] = [];
  try {
    fundSearchResults = await promiseFetchFundSearchResults;
  } catch (e) {
    err = e;
  } finally {
    commit('setNumberOfFetching', getters['numberOfFetching'] - 1);
  }

  if (!err && fundSearchResults.length > 0) {
    commit('setSelectedResultIndex', -1);
    commit('results', fundSearchResults);

    const mustHaveCacheSuffix = getters['mustHaveCacheSuffix'];
    if (!mustHaveCacheSuffix || !!cacheSuffix) {
      cache.set(searchText, fundSearchResults);
    }
  } else {
    if (!err && fundSearchResults.length === 0) {
      commit('setLastFetchError', 'no result');
    } else {
      commit('setLastFetchError', err);
    }
  }
  cache.flushExpired();
}

const actions: ActionTree<IFundSearchState, any> = {
  setupClassCommon() {
    setupClassCommon();
  },
  unsetClassCommon() {
    unsetClassCommon();
  },
  selectPreviousResult({ commit, getters }) {
    const results = getters.results;
    let current = getters.selectedResultIndex;
    if (current < 0) {
      current = results?.length || -1;
    }
    if (current > 0) {
      commit('setSelectedResultIndex', current - 1);
    } else {
      commit('setSelectedResultIndex', -1);
    }
  },
  selectNextResult({ commit, getters }) {
    const current = getters.selectedResultIndex;
    const results = getters.results;
    const upperBound = results?.length || 0;
    if (current < upperBound - 1) {
      commit('setSelectedResultIndex', current + 1);
    } else {
      commit('setSelectedResultIndex', -1);
    }
  },
  async lookup({ commit, getters }, searchText: string) {
    commit('results', []); // reset result
    if (timeoutHandle.debounce) {
      clearTimeout(timeoutHandle.debounce);
      timeoutHandle.debounce = 0;
    }
    timeoutHandle.debounce = setTimeout(
      async () => await performSearch(searchText, commit, getters),
      debounceTime
    );
  },
};

export default actions;
